import React, { useState } from 'react';
import { Snackbar, Alert, Box, Button, TextField, CircularProgress } from '@mui/material';
import { Grid } from '@mui/system';
import { gql, useMutation } from '@apollo/client';
import GoogleMapsEmbed from '../../../components/ GoogleMapsEmbed';
import mobilbg from '../../../assets/svg/mapbg.svg';

const CREATE_CONTACT = gql`
 mutation CreateContactForm($data: ContactFormInput!) {
  createContactForm(data: $data) {
    email
    message
    name
  }
}
`;

function ContactForm({ contactExperts }) {
  const [createContact, { loading: formLoading }] = useMutation(CREATE_CONTACT);
  // Toaster state
  const [toaster, setToaster] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleCloseToaster = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToaster({ ...toaster, open: false });
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
  });

  // Form touched state to track which fields user has interacted with
  const [touched, setTouched] = useState({
    name: false,
    email: false,
  });

  // Validation function for individual fields
  const validateField = (field, value) => {
    let errorMessage = '';

    switch (field) {
      case 'name':
        if (!value.trim()) {
          errorMessage = 'Name is required';
        } else if (value.trim().length < 2) {
          errorMessage = 'Name must be at least 2 characters';
        } else if (value.trim().length > 50) {
          errorMessage = 'Name must be less than 50 characters';
        } else if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) {
          errorMessage = 'Name contains invalid characters';
        }
        break;
      case 'email':
        if (!value.trim()) {
          errorMessage = 'Email is required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
          errorMessage = 'Invalid email address';
        } else if (value.length > 100) {
          errorMessage = 'Email must be less than 100 characters';
        }
        break;
      default:
        break;
    }

    setFormErrors((prev) => ({
      ...prev,
      [field]: errorMessage,
    }));

    return !errorMessage;
  };

  // Add validateForm function to check all required fields
  const validateForm = () => {
    const fields = ['name', 'email'];
    let isValid = true;
    const newTouched = { ...touched };
    const newErrors = { ...formErrors };

    fields.forEach((field) => {
      newTouched[field] = true;
      const fieldIsValid = validateField(field, formData[field]);
      if (!fieldIsValid) {
        isValid = false;
      }
    });

    // Update touched state to show all errors
    setTouched(newTouched);
    setFormErrors(newErrors);

    return isValid;
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value,
    });

    // Mark field as touched
    if (!touched[id]) {
      setTouched({
        ...touched,
        [id]: true,
      });
    }

    // Validate on change for better user experience
    validateField(id, value);
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;

    // Mark field as touched
    setTouched({
      ...touched,
      [id]: true,
    });

    // Validate on blur
    validateField(id, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    if (!validateForm()) {
      setToaster({
        open: true,
        message: 'Please fill in all required fields correctly',
        severity: 'error',
      });
      return;
    }

    try {
      await createContact({
        variables: {
          data: formData,
        },
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        message: '',
      });

      // Reset touched state
      setTouched({
        name: false,
        email: false,
      });

      // Reset errors
      setFormErrors({
        name: '',
        email: '',
      });

      setToaster({
        open: true,
        message: 'Thank you! Your message has been sent successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error submitting contact form', err);
      setToaster({
        open: true,
        message: err.message || 'Failed to send message. Please try again later.',
        severity: 'error',
      });
    }
  };

  return (
    <Grid
      className="contact-form-section-wrap"
      sx={{
        backgroundImage: { xs: `url(${mobilbg})`, sm: `url(${contactExperts?.backgroundImage?.url})` },
      }}

    >
      <Grid container>
        <Grid size={{ xs: 12, md: 5.5, sm: 7 }}>
          <div className="title">{contactExperts?.title}</div>
          <div className="subTitle">{contactExperts?.subTitle}</div>

          <Box sx={{ display: 'flex', gap: '24px', flexDirection: 'column', my: '24px' }}>
            <TextField
              id="name"
              label="Name"
              variant="outlined"
              value={formData.name}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={touched.name && !!formErrors.name}
              helperText={touched.name && formErrors.name}
              sx={{ width: '100%' }}
              required
              inputProps={{ maxLength: 50 }}
              />
            <TextField
              id="email"
              label="Email"
              variant="outlined"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={touched.email && !!formErrors.email}
              helperText={touched.email && formErrors.email}
              sx={{ width: '100%' }}
              required
              inputProps={{ maxLength: 100 }}
              />
            <TextField
              multiline
              rows={3}
              id="message"
              label="Message"
              variant="outlined"
              placeholder="Enter your message"
              value={formData.message}
              onChange={handleInputChange}
              sx={{ width: '100%' }}
            />
            <Button
              size="large"
              variant="contained"
              disabled={formLoading}
              onClick={handleSubmit}
              sx={{
                position: 'relative',
                '&:disabled': {
                  backgroundColor: '#114559',
                  opacity: 0.7,
                  color: 'white',
                },
              }}
              >
              {formLoading ? (
                <>
                  <CircularProgress
                    size={24}
                    sx={{
                      color: 'white',
                      position: 'absolute',
                      left: '50%',
                      marginLeft: '-12px',
                    }}
                    />
                  <span style={{ visibility: 'hidden' }}>Sending...</span>
                </>
              ) : contactExperts?.button?.label?.slice(0, 25) || 'Submit'}
            </Button>
            <Grid container justifyContent={'space-between'} rowSpacing={3} columnSpacing={1}>
              {contactExperts?.contactModes?.map((item) => (
                <Grid size={{ xs: 6, sm: 4 }} key={item?.label || item?.id}>
                  <div className="mode-item-wrap">
                    <div>
                      <img src={item?.icon?.url} alt={item?.icon?.name} />
                    </div>
                    <div>
                      <div className="label">{item?.label}</div>
                      {item?.description && item?.description.includes('@') ? (
                        <div>
                          <a className="details" style={{ textDecoration: 'none' }} href={`mailto:${item?.description}`}>{item?.description}</a>
                        </div>
                      ) : (
                        <div className="details">{item?.description}</div>
                      )}
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
        <Grid size={1} />
        <Grid size={{ xs: 12, sm: 4 }} className="map-wrap">
          <GoogleMapsEmbed />
        </Grid>
      </Grid>
      <Snackbar
        open={toaster.open}
        autoHideDuration={6000}
        onClose={handleCloseToaster}
        >
        <Alert onClose={handleCloseToaster} severity={toaster.severity}>
          {toaster.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default ContactForm;
